'use client';

import { type ErrorMessageField, getErrorMessage } from '@/app/cc/_util/getErrorMessage';
import type { ErrorData, FetchError } from '@/lib/cc/commonType';
import { useAlertModal } from './useAlertModal';

interface MutationErrorRule {
  readonly status?: number;
  readonly code?: number;
  readonly fallback: string;
  readonly messageFrom?: ReadonlyArray<ErrorMessageField>;
  readonly beforeAlert?: () => void;
}

interface HandleMutationErrorOptions {
  readonly defaultMessage: string;
  readonly defaultMessageFrom?: ReadonlyArray<ErrorMessageField>;
  readonly rules?: ReadonlyArray<MutationErrorRule>;
}

function parseFetchError(error: unknown): {
  status?: number;
  code?: number;
  data?: Partial<ErrorData>;
} {
  const fetchError = error as Partial<FetchError<ErrorData>>;
  const statusCode = typeof fetchError?.data?.statusCode === 'number' ? fetchError.data.statusCode : undefined;
  const code = typeof fetchError?.data?.code === 'number' ? fetchError.data.code : undefined;

  return {
    status: typeof fetchError?.status === 'number' ? fetchError.status : undefined,
    code: code ?? statusCode,
    data: fetchError?.data,
  };
}

function isMatchedRule(rule: MutationErrorRule, target: { readonly status?: number; readonly code?: number }) {
  const matchesStatus = rule.status === undefined || rule.status === target.status;
  const matchesCode = rule.code === undefined || rule.code === target.code;
  return matchesStatus && matchesCode;
}

export function useMutationErrorAlert() {
  const openAlert = useAlertModal();

  return (error: unknown, options: HandleMutationErrorOptions): void => {
    const parsed = parseFetchError(error);
    const matchedRule = options.rules?.find((rule) => isMatchedRule(rule, parsed));

    if (matchedRule) {
      matchedRule.beforeAlert?.();
      const message = getErrorMessage(parsed.data, matchedRule.messageFrom) ?? matchedRule.fallback;
      openAlert(message);
      return;
    }

    const message =
      getErrorMessage(parsed.data, options.defaultMessageFrom ?? ['desc', 'result', 'message']) ??
      options.defaultMessage;
    openAlert(message);
  };
}
